import { useCallback, useMemo, useState } from 'react';

import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import type {
  PrimaryFilterId,
  TreeSelection,
} from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import { isOverlappingTreemapSegment } from '../../../filters/utils/treemapDrilldownLevels';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { dimensionFromPrimaryFilter } from '../../DataResolver/convert-composition-v4-data';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import { taxonomyOf } from '../../DataResolver/taxonomy-levels';
import {
  findLevelOfLadderId,
  useCompositionV4Drilldown,
} from '../../DataResolver/use-composition-v4-drilldown';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { useChart } from '../ChartProvider';
import { formatColumnName } from '../chart-menu/data-download/converters/utils';
import type { DrilldownEntry } from '../utils/composition-drilldown';
import { buildTableIcicle, type TableIcicleEntity } from './build-table-icicle';
import { IcicleContent } from './IcicleContent';
import type { IcicleDrillStep } from './icicle-data';
import type { IcicleSegment } from './icicle-segment';
import { useIcicleChartKey, useIcicleChips } from './use-icicle-chips';

const ICICLE_METRICS: readonly MetricValues[] = [
  'headcount',
  'share_of_headcount',
];

const UNSERVED_DRILL: ResolvedData = {
  data: undefined,
  status: DataProviderQueryStatus.Error,
};

type TableIcicleProps = {
  data: ResolvedData;
  segment: IcicleSegment;
  primaryDimension: PrimaryFilterId;
};

const TableIcicleChart = ({
  data,
  segment,
  primaryDimension,
  chips,
}: TableIcicleProps & { chips: readonly TreeSelection[] }) => {
  const { item } = useChart();
  const { geographySelectionListIds } = useFilterContext();
  const [drilldownStack, setDrilldownStack] = useState<DrilldownEntry[]>([]);
  const drilledInto = drilldownStack[drilldownStack.length - 1];
  const drilled = useCompositionV4Drilldown({
    primaryDimension,
    dimension: segment,
    drilledInto,
    metrics: ICICLE_METRICS,
    sublevel: true,
  });
  const view = drilledInto === undefined ? data : (drilled ?? UNSERVED_DRILL);

  const entities = useMemo(
    (): TableIcicleEntity[] =>
      chips.map((chip) => ({
        id: String(getNumericIdFromNestedId(chip.id) ?? chip.id),
        name: chip.name,
        level:
          dimensionFromPrimaryFilter(primaryDimension) === segment
            ? taxonomyOf(segment, geographySelectionListIds)?.levelOf(
                chip.levelId
              )
            : undefined,
      })),
    [chips, primaryDimension, segment, geographySelectionListIds]
  );

  const taxonomyCoverage = item?.plot?.treemap?.taxonomyCoverage;
  const icicle = useMemo(() => {
    if (view.status !== DataProviderQueryStatus.Success || !view.data) {
      return undefined;
    }
    return buildTableIcicle({
      table: view.data,
      entities,
      drilledInto: drilledInto && {
        level: findLevelOfLadderId(
          segment,
          drilledInto.level,
          geographySelectionListIds
        ),
        id: String(drilledInto.id),
      },
      drillPath: drilldownStack.map(({ level, id, name }) => ({
        levelId: level,
        id: String(id),
        name,
      })),
      overlapping: isOverlappingTreemapSegment(segment),
      coverage: taxonomyCoverage,
      geographySelectionListIds,
      entityLabel: formatColumnName(primaryDimension),
    });
  }, [
    view,
    entities,
    drilledInto,
    drilldownStack,
    segment,
    taxonomyCoverage,
    geographySelectionListIds,
    primaryDimension,
  ]);

  const handleNavigate = useCallback((targetDepth: number) => {
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  const handleDrill = useCallback(
    (path: readonly IcicleDrillStep[], color: string) => {
      setDrilldownStack((prev) => [
        ...prev,
        ...path.map(
          (step): DrilldownEntry => ({
            level: step.levelId,
            id: step.id,
            name: step.name,
            segment,
            color,
          })
        ),
      ]);
    },
    [segment]
  );

  return (
    <IcicleContent
      data={icicle}
      status={view.status}
      drilldownStack={drilldownStack}
      onDrill={handleDrill}
      onNavigate={handleNavigate}
      sizeTransform={item?.plot?.icicle?.sizeTransform}
    />
  );
};

export const TableIcicle = ({
  data,
  segment,
  primaryDimension,
}: TableIcicleProps) => {
  const chips = useIcicleChips();
  const chartKey = useIcicleChartKey(segment, chips);
  if (chips.length === 0) return null;
  return (
    <TableIcicleChart
      key={chartKey}
      data={data}
      segment={segment}
      primaryDimension={primaryDimension}
      chips={chips}
    />
  );
};
